import React, { useRef, useEffect, useCallback } from "react";

interface RichInputProps {
  value: string;
  onChange: (val: string) => void;
  minHeight?: string;
  placeholder?: string;
  className?: string;
}

export function RichInput({
  value,
  onChange,
  minHeight = "80px",
  placeholder = "",
  className,
}: RichInputProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  // Convert internal ATS Markdown to HTML for display
  const toHtml = useCallback((text: string) => {
    let html = text || "";
    
    // Fix any literal \n that might come from the AI
    html = html.replace(/\\n/g, "\n");

    // Simple naive unescaping if needed, but usually we just want to replace our tags
    html = html.replace(/\*\*([\s\S]*?)\*\*/g, "<b>$1</b>");

    // We leave <del> and <ins> as is because they are valid HTML elements

    // Replace newlines with <br> for display
    html = html.replace(/\n/g, "<br>");
    return html;
  }, []);

  // Convert HTML back to ATS Markdown
  const toMarkdown = useCallback((html: string) => {
    let md = html;

    // Replace b/strong with our markers
    md = md.replace(/<(b|strong)\b[^>]*>([\s\S]*?)<\/\1>/gi, "**$2**");

    // Replace strike with del
    md = md.replace(/<strike\b[^>]*>([\s\S]*?)<\/strike>/gi, "<del>$1</del>");

    // Handle block elements
    // First, remove <br> that are solely inside a block tag
    md = md.replace(
      /<(div|p|h[1-6]|li|blockquote)\b[^>]*>\s*<br\s*\/?>\s*<\/\1>/gi,
      "\n",
    );

    // Replace p closing tags with \n
    md = md.replace(/<\/p>/gi, "\n");

    // Replace block closing tags with \n
    md = md.replace(/<\/(div|h[1-6]|li|blockquote)>/gi, "\n");

    // Replace <br> with \n
    md = md.replace(/<br\s*\/?>/gi, "\n");

    // Strip all remaining HTML tags
    md = md.replace(/<(?!\/?(?:del|ins)\b)[^>]+>/gi, "");

    // Decode HTML entities
    md = md.replace(/&nbsp;/g, " ");
    md = md.replace(/&lt;/g, "<");
    md = md.replace(/&gt;/g, ">");
    md = md.replace(/&amp;/g, "&");

    // Ensure bold tags don't span across multiple lines (which breaks generic markdown parsers like PDF)
    md = md.replace(/\*\*([\s\S]*?)\*\*/g, (match, inner) => {
      return inner
        .split("\n")
        .map((line: string) => (line.trim() ? `**${line}**` : line))
        .join("\n");
    });

    // Clean up empty bolds that contain only spaces or tabs
    md = md.replace(/\*\*([ \t]*)\*\*/g, "$1");
    md = md.replace(/\*\*\*\*/g, "");

    // Clean up trailing and leading newlines/spaces if necessary, but keep internal spacing
    md = md.trim();

    return md;
  }, []);

  useEffect(() => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.innerHTML = toHtml(value);
    }
  }, [value, toHtml]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(toMarkdown(editorRef.current.innerHTML));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      // In some browsers, Enter creates divs, let's coerce it just to insert a <br>
      // if we want to bypass default behavior
    }

    // Add support for Cmd/Ctrl+B
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
      e.preventDefault();
      document.execCommand("bold", false, undefined);
    }
  };

  return (
    <div
      ref={editorRef}
      contentEditable
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        // re-format on blur to clean up any messy HTML
        if (editorRef.current) {
          const md = toMarkdown(editorRef.current.innerHTML);
          onChange(md);
          editorRef.current.innerHTML = toHtml(md);
        }
      }}
      role="textbox"
      className={
        className ||
        "w-full relative p-3 border border-slate-200 dark:border-slate-600 rounded-lg bg-slate-100 dark:bg-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none text-sm text-slate-900 dark:text-white transition-all dark:shadow-sm"
      }
      style={{ minHeight, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      data-placeholder={placeholder}
    />
  );
}
